<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { formatDateShort, formatMoney } from '@shared/utils'
import PropertyStatusChip from './PropertyStatusChip.vue'
import { fundingStatusMetaOf } from '../../constants/propertyLifecycle'
import type { PropertySummary } from '../../types/propertyTypes'

// One row of the Property Portfolio ledger. Presentational — property identity,
// listing price, lifecycle status, and "View Details". Emits `view` with the id.
//
// The funding line only renders for a property that actually has an offering: a
// property still in review is not "0% funded", it is not raising at all.
const props = defineProps<{ property: PropertySummary }>()
const emit = defineEmits<{ (e: 'view', id: string): void }>()

const location = computed(
  () => [props.property.areaName, props.property.city].filter(Boolean).join(', ') || '—',
)

// The promotion date is when this became a property. Falls back to a dash rather
// than to "now" — a missing stamp is a real state, not something to invent.
const promotedOn = computed(() =>
  props.property.promotedAt ? formatDateShort(props.property.promotedAt) : '—',
)

// Price lives on the originating request: promotion deliberately does not copy it
// onto the property row.
const listingPrice = computed(() => {
  const { priceAgreed, currencyCode } = props.property
  if (!priceAgreed) return 'Not set'
  return formatMoney(currencyCode, Number(priceAgreed))
})

const fundingMeta = computed(() => fundingStatusMetaOf(props.property.offeringProcessingStatus))

const fundingProgress = computed(() => props.property.fundingProgressPercentage)
</script>

<template>
  <Box is="article" flex align="center" gap="4" class="property-row">
    <Box direction="col" gap="2" class="property-row__identity">
      <Text is="h3" type="body" weight="semibold" truncate>{{ property.propertyName }}</Text>
      <Box flex align="center" gap="3" class="property-row__meta">
        <Box flex align="center" gap="1">
          <PiIcon name="map-marker" class="property-row__meta-icon" />
          <Text type="caption" color="muted" truncate>{{ location }}</Text>
        </Box>
        <Box flex align="center" gap="1">
          <PiIcon name="calendar" class="property-row__meta-icon" />
          <Text type="caption" color="muted">{{ promotedOn }}</Text>
        </Box>
      </Box>
    </Box>

    <Box direction="col" gap="1" class="property-row__price">
      <Text type="body" weight="semibold">{{ listingPrice }}</Text>
      <Text type="caption-sm" color="muted">Listing price</Text>
    </Box>

    <Box direction="col" gap="2" grow class="property-row__status">
      <PropertyStatusChip :stage="property.stage" size="small" />
      <Box v-if="fundingMeta" flex align="center" gap="2" class="property-row__funding">
        <Text type="caption-sm" color="muted">{{ fundingMeta.label }}</Text>
        <Text v-if="fundingProgress !== null" type="caption-sm" color="muted">
          · {{ fundingProgress }}% subscribed
        </Text>
      </Box>
    </Box>

    <Button
      label="View Details"
      variant="outline"
      size="small"
      class="property-row__action"
      @click="emit('view', property.id)"
    />
  </Box>
</template>

<style scoped>
.property-row {
  padding: 1.25rem;
  border: 1px solid var(--p-content-border-color);
  border-left: 3px solid var(--p-primary-300);
  border-radius: var(--p-border-radius-lg);
  background: var(--p-surface-0);
}

.property-row__identity {
  flex: 0 0 auto;
  width: 300px;
  min-width: 0;
  padding-right: 1rem;
}

.property-row__meta-icon {
  font-size: 11px;
  color: var(--p-text-muted-color);
}

.property-row__price {
  flex: 0 0 auto;
  width: 170px;
  min-width: 0;
}

.property-row__status {
  min-width: 0;
  align-items: flex-start;
}

.property-row__action {
  flex-shrink: 0;
}

@media (max-width: 900px) {
  .property-row {
    flex-direction: column;
    align-items: stretch;
  }

  .property-row__identity,
  .property-row__price {
    width: 100%;
    padding-right: 0;
  }

  .property-row__identity {
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--p-content-border-color);
  }

  .property-row__action {
    align-self: flex-start;
  }
}
</style>
